import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { CONF_MENU_CHANGE } from '../../settings/events';
import { findItem, getMenuItems } from './getMenuItems';
import { Icon } from '../Icon';
import { TOP } from '../Popup/alignments';
import { translate } from '../../services/L10n';
import emitter from '../../emitter';
import productService from '../../services/products';
import StopPropagation from '../utils/StopPropagation';
import Tooltip from '../Popup/Tooltip';
import styles from './ConfMenu.module.less';
import { thunkHideItem, thunkRemoveItem } from '../../state/tac/tacThunks';
import { ADDITIONAL_INFO } from '../Sheets/AdditionalInfoSheet';
import localStatisticsReporter from '../../services/statistics/insights/custom/local/localStatisticsReporter';
import { selectTac } from '../../state/tac/tacSelectors';
import { COMBINED_PRODUCT_INFO } from '../Sheets/CombinedProductsSheet';
import { isCombinedItem } from '../../state/products/productsHelpers';
import { SheetObject } from '../../state/sheets/sheetTypes';
import { actionEnqueueSheet } from '../../state/sheets/sheetActions';
import { applicationSettings } from '../../settings/application';
import { RANGES } from '../../constants';
import { t } from '../../translations';

interface Props {
  container: any;
  onDelete: (item: any) => {};
}

const ConfMenu: React.FunctionComponent<Props> = ({ container, onDelete }) => {
  const [showAdditionalInformation, setShowAdditionalInformation] =
    React.useState(false);

  const dispatch = useDispatch();

  const tac = useSelector(selectTac);
  const item = findItem(tac, container);

  const hideItem = (item: any) =>
    // @ts-ignore
    dispatch(thunkHideItem(item, undefined, { origin: 'dialog' }));
  const removeItem = (item: any) =>
    // @ts-ignore
    dispatch(thunkRemoveItem(item, undefined, { origin: 'dialog' }));

  React.useEffect(() => {
    emitter.emit(CONF_MENU_CHANGE);
  }, [item]);

  if (!item) return null;

  const menuItems = getMenuItems(tac, container);
  const isSection = productService.isSection(item);

  /**
   * Handles on delete click
   */
  const onDeleteClick = () => {
    hideItem(item);
    setTimeout(() => {
      removeItem(item);
    }, 200);

    onDelete(item);
  };

  /**
   * Handles info click
   */
  const onInfoClick = () => {
    _showInfo();
    localStatisticsReporter.reportConfMenuInfoButtonClicked(container.item.id);
  };

  /**
   * Hide additional information
   */
  const _hideAdditionalInformation = () => setShowAdditionalInformation(false);

  const _showCombinedProductInfo = () => {
    const sheetObject: SheetObject = {
      sheetType: COMBINED_PRODUCT_INFO,
      item,
      onOverlayClick: _hideAdditionalInformation,
      displaySheet: showAdditionalInformation,
      onCloseButtonClick: _hideAdditionalInformation,
    };
    dispatch(actionEnqueueSheet(sheetObject));
  };

  /**
   * Render additional information
   */
  const _showAdditionalInfo = () => {
    const sheet: SheetObject = {
      sheetType: ADDITIONAL_INFO,
      item,
    };
    dispatch(actionEnqueueSheet(sheet));
  };

  const isIvar = () => applicationSettings.applicationName === RANGES.IVAR;

  const _showInfo = () => {
    isIvar() && isCombinedItem(item)
      ? _showCombinedProductInfo()
      : _showAdditionalInfo();
  };

  /**
   * Render info icon
   */
  const renderInfoIcon = () => (
    <div className={styles.iconContainer}>
      <Tooltip
        key={'info'}
        alignment={TOP}
        spacing={-2}
        ignorePadding={true}
        content={translate(t.TOOLTIP_INFO)}
      >
        <div
          className={classNames(styles.icon)}
          data-testid={'info-icon'}
          onClick={onInfoClick}
        >
          <Icon iconName={'InformationCircleIcon'} />
        </div>
      </Tooltip>
    </div>
  );

  /**
   * Render trashcan
   */
  const renderTrashCan = () => (
    <div className={styles.iconContainer}>
      <Tooltip
        key={'trashcan'}
        alignment={TOP}
        spacing={-2}
        ignorePadding={true}
        content={
          isSection
            ? translate(t.TOOLTIP_DELETE_SECTION)
            : translate(t.TOOLTIP_DELETE)
        }
      >
        <div
          className={classNames(styles.icon)}
          data-testid={'trash-icon'}
          onClick={onDeleteClick}
        >
          <Icon iconName={'TrashCanIcon'} />
        </div>
      </Tooltip>
    </div>
  );

  return item ? (
    <>
      <StopPropagation className={classNames(styles.confGroup)}>
        {!!menuItems.showInfoIcon && renderInfoIcon()}
        {menuItems.options}
        {menuItems.showTrashCan && renderTrashCan()}
      </StopPropagation>
    </>
  ) : null;
};

export default ConfMenu;
